import { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Searchable combo select — handles 400+ items smoothly with virtualized-like filtering.
 * Props:
 *  - value: current selected value
 *  - onChange: callback(value)
 *  - options: [{ value, label, secondary? }]
 *  - placeholder: search placeholder
 *  - label: field label
 *  - testId: data-testid
 */
export function ComboSelect({ value, onChange, options, placeholder = 'Search...', label, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = options.find(o => o.value === value)?.label || value || 'Select...';

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = search
    ? options.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        o.value.toLowerCase().includes(search.toLowerCase()) ||
        (o.secondary && o.secondary.toLowerCase().includes(search.toLowerCase()))
      )
    : options;

  const select = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white transition-colors text-left",
          open ? "ring-2 ring-emerald-500/20 border-emerald-500" : "hover:border-gray-300"
        )}
        data-testid={testId}
      >
        <span className="truncate text-text-primary">{selectedLabel}</span>
        <ChevronDown className={cn("h-4 w-4 text-gray-400 flex-shrink-0 ml-2 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" style={{ maxHeight: '320px' }}>
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={placeholder}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '264px' }}>
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-4">No results found</p>
            ) : (
              filtered.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => select(opt.value)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                    opt.value === value && "bg-emerald-50 text-emerald-700"
                  )}
                >
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.value === value && <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
